// Bloomberg-inspired palette: golds/yellows as primary accent, muted red for urgency, muted blue for softer categories

type CategoryColorSet = { bg: string; text: string; border: string; tileBg: string; accent: string; hex: string };

// Manually mapped categories keep their intentional colors
export const categoryColors: Record<string, CategoryColorSet> = {
  Latest:     { bg: "bg-amber-400/15", text: "text-amber-400",   border: "border-amber-400/30", tileBg: "bg-transparent", accent: "border-l-amber-400", hex: "#fbbf24" },
  Trending:   { bg: "bg-amber-500/15", text: "text-amber-500",   border: "border-amber-500/30", tileBg: "bg-transparent", accent: "border-l-amber-500", hex: "#f59e0b" },
  Politics:   { bg: "bg-rose-400/8",   text: "text-rose-300",    border: "border-rose-400/15",  tileBg: "bg-transparent", accent: "border-l-rose-400/60", hex: "#fb7185" },
  Crypto:     { bg: "bg-yellow-500/12", text: "text-yellow-500", border: "border-yellow-500/25", tileBg: "bg-transparent", accent: "border-l-yellow-500", hex: "#eab308" },
  Financial:  { bg: "bg-sky-400/12",   text: "text-sky-400",     border: "border-sky-400/25",   tileBg: "bg-transparent", accent: "border-l-sky-400",    hex: "#38bdf8" },
  "Tech & AI":{ bg: "bg-amber-300/12", text: "text-amber-300",   border: "border-amber-300/25", tileBg: "bg-transparent", accent: "border-l-amber-300", hex: "#fcd34d" },
  Culture:    { bg: "bg-slate-400/8",  text: "text-slate-300",   border: "border-slate-400/15", tileBg: "bg-transparent", accent: "border-l-slate-400/60", hex: "#94a3b8" },
  Regulation: { bg: "bg-orange-300/10", text: "text-orange-300",  border: "border-orange-300/20", tileBg: "bg-transparent", accent: "border-l-orange-300/60", hex: "#fdba74" },
  Industry:   { bg: "bg-amber-400/10", text: "text-amber-400",   border: "border-amber-400/20", tileBg: "bg-transparent", accent: "border-l-amber-400", hex: "#fbbf24" },
  General:    { bg: "bg-slate-400/10", text: "text-slate-400",   border: "border-slate-400/20", tileBg: "bg-transparent", accent: "border-l-slate-400", hex: "#94a3b8" },
};

// Curated rotation palette for any new/unknown categories. Muted, professional tones only.
const autoPalette: CategoryColorSet[] = [
  { bg: "bg-amber-400/12", text: "text-amber-400",  border: "border-amber-400/25", tileBg: "bg-transparent", accent: "border-l-amber-400",    hex: "#fbbf24" },
  { bg: "bg-sky-400/10",   text: "text-sky-300",    border: "border-sky-400/20",   tileBg: "bg-transparent", accent: "border-l-sky-400/60",   hex: "#7dd3fc" },
  { bg: "bg-slate-400/10", text: "text-slate-400",  border: "border-slate-400/20", tileBg: "bg-transparent", accent: "border-l-slate-400",    hex: "#94a3b8" },
  { bg: "bg-amber-300/10", text: "text-amber-300",  border: "border-amber-300/20", tileBg: "bg-transparent", accent: "border-l-amber-300",    hex: "#fcd34d" },
  { bg: "bg-rose-400/8",   text: "text-rose-300",   border: "border-rose-400/15",  tileBg: "bg-transparent", accent: "border-l-rose-400/60",  hex: "#fb7185" },
  { bg: "bg-zinc-400/10",  text: "text-zinc-400",   border: "border-zinc-400/20",  tileBg: "bg-transparent", accent: "border-l-zinc-400",     hex: "#a1a1aa" },
  { bg: "bg-yellow-500/10",text: "text-yellow-500", border: "border-yellow-500/20",tileBg: "bg-transparent", accent: "border-l-yellow-500",   hex: "#eab308" },
];

// Simple deterministic hash so the same category name always gets the same color
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

// Cache auto-assigned colors so the lookup is stable within a session
const autoAssigned = new Map<string, CategoryColorSet>();

// Build a color set from a raw hex value (used for Sanity overrides)
function colorSetFromHex(hex: string): CategoryColorSet {
  return {
    bg: "bg-transparent",
    text: "",
    border: "",
    tileBg: "bg-transparent",
    accent: "",
    hex,
  };
}

/** Register a Sanity-provided hex override for a category */
export function registerCategoryColor(category: string, hex: string) {
  if (!hex || categoryColors[category]) return; // don't override hardcoded
  autoAssigned.set(category, colorSetFromHex(hex));
}

export const getCategoryColor = (category: string): CategoryColorSet => {
  if (categoryColors[category]) return categoryColors[category];
  if (autoAssigned.has(category)) return autoAssigned.get(category)!;
  const color = autoPalette[hashString(category) % autoPalette.length];
  autoAssigned.set(category, color);
  return color;
};

export const getCategoryBadgeClass = (category: string) => {
  const c = getCategoryColor(category);
  return `${c.bg} ${c.text} ${c.border}`;
};
